import { useEffect, useRef } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'

const SIZE = 24
const RADIUS = SIZE / 2
const BORDER = 2

const CanvasLogosLayer = ({ points, hiddenPlatformModules }) => {
    const map = useMap()
    const canvasRef = useRef(null)
    const imagesCache = useRef({})
    const pointsRef = useRef(points)
    pointsRef.current = points
    const hiddenRef = useRef(hiddenPlatformModules)
    hiddenRef.current = hiddenPlatformModules

    useEffect(() => {
        const canvas = document.createElement('canvas')
        canvas.style.position = 'absolute'
        canvas.style.top = '0'
        canvas.style.left = '0'
        canvas.style.pointerEvents = 'none'
        canvas.classList.add('leaflet-zoom-animated', 'leaflet-layer')
        canvasRef.current = canvas

        map.getPanes().overlayPane.appendChild(canvas)

        function getImage(platformModule) {
            if (!imagesCache.current[platformModule]) {
                const img = new Image()
                img.src = `/img/platforms/${platformModule}.jpg`
                img.onload = () => draw()
                imagesCache.current[platformModule] = img
            }
            return imagesCache.current[platformModule]
        }

        function draw() {
            if (!canvasRef.current) return
            const ctx = canvas.getContext('2d')

            const mapSize = map.getSize()
            canvas.width = mapSize.x
            canvas.height = mapSize.y

            const topLeft = map.containerPointToLayerPoint([0, 0])
            L.DomUtil.setPosition(canvas, topLeft)

            ctx.clearRect(0, 0, canvas.width, canvas.height)

            const pts = pointsRef.current
            if (!pts || pts.length === 0) return

            pts
              .filter(p => !hiddenRef.current || !hiddenRef.current.has(p.platformModule))
              .forEach(point => {
                const cp = map.latLngToContainerPoint([
                    point.location.latitude,
                    point.location.longitude,
                ])

                ctx.beginPath()
                ctx.arc(cp.x, cp.y, RADIUS + BORDER, 0, Math.PI * 2)
                ctx.fillStyle = 'white'
                ctx.fill()

                ctx.save()
                ctx.beginPath()
                ctx.arc(cp.x, cp.y, RADIUS, 0, Math.PI * 2)
                ctx.clip()
                const img = getImage(point.platformModule)
                if (img.complete && img.naturalWidth > 0) {
                    ctx.drawImage(img, cp.x - RADIUS, cp.y - RADIUS, SIZE, SIZE)
                } else {
                    ctx.fillStyle = '#ccc'
                    ctx.fill()
                }
                ctx.restore()

                ctx.beginPath()
                ctx.arc(cp.x, cp.y, RADIUS + BORDER, 0, Math.PI * 2)
                ctx.strokeStyle = 'rgba(0,0,0,0.35)'
                ctx.lineWidth = 1
                ctx.stroke()
            })
        }

        canvasRef.current._draw = draw

        function onZoomStart() {
            canvas.style.display = 'none'
        }
        function onZoomEnd() {
            draw()
            canvas.style.display = ''
        }

        map.on('zoomstart', onZoomStart)
        map.on('zoomend', onZoomEnd)
        map.on('moveend resize', draw)

        draw()

        return () => {
            map.off('zoomstart', onZoomStart)
            map.off('zoomend', onZoomEnd)
            map.off('moveend resize', draw)
            if (canvas.parentNode) canvas.parentNode.removeChild(canvas)
            canvasRef.current = null
        }
    }, [map]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        canvasRef.current?._draw?.()
    }, [points, hiddenPlatformModules])

    return null
}

export { CanvasLogosLayer }
